// @systeme: parametres-organisation
// ============================================================================
// PairsForm — Dialogue d'acceptation des conditions d'usage (lot 8.5, décision F)
// ============================================================================
// Présenté au PILOTE d'une organisation cliente tant que la version en vigueur
// des conditions d'usage n'a pas été acceptée au nom de l'org (le serveur
// décide : GET /conditions-usage/etat, P8 — org du jeton). « Plus tard » le
// reporte pour cette session de navigation ; la section « Conditions d'usage »
// de Mon organisation garde l'état visible. Jamais en immersion : un pilote
// Cecodev qui visite n'engage pas le client (le serveur le refuse aussi).
// ============================================================================

import { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, Link as MuiLink, Typography } from "@mui/material";
import { toast } from "sonner";
import { api, messageApi } from "../lib/api";
import { DialogMobile } from "./ui/DialogMobile";
import { useAuth } from "../contexts/AuthContext";
import { formatDateShortFR } from "@cecodev/shared";

interface VersionConditionsUsage {
  id: string;
  titre: string;
  version: string;
  dateEffet: string;
}
interface EtatConditionsUsage {
  requis: boolean;
  version: VersionConditionsUsage | null;
  aAccepter: boolean;
}

/** Orgs pour lesquelles le pilote a cliqué « Plus tard » dans cette session de navigation. */
const reportees = new Set<string>();

export function ConditionsUsageDialog() {
  const { user, isLoading } = useAuth();
  const [version, setVersion] = useState<VersionConditionsUsage | null>(null);
  const [texte, setTexte] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [busy, setBusy] = useState(false);

  const orgId = user?.organisationId ?? null;
  const estPiloteDeLOrg = !!user && !user.impersonating && user.roles.some((r) => r === "PILOTE" || r === "SUPER_ADMIN");

  useEffect(() => {
    if (isLoading || !orgId || !estPiloteDeLOrg || reportees.has(orgId)) {
      setOuvert(false);
      return;
    }
    let annule = false;
    (async () => {
      try {
        const etat = (await api.get("/conditions-usage/etat")).data as EtatConditionsUsage;
        if (annule || !etat.aAccepter || !etat.version) return;
        const doc = (await api.get("/documents-legaux/CONDITIONS_USAGE")).data as { contenu: string };
        if (annule) return;
        setVersion(etat.version);
        setTexte(doc.contenu);
        setOuvert(true);
      } catch {
        // Silencieux : la page reste utilisable, la section « Conditions
        // d'usage » de Mon organisation montre l'état et porte le bouton.
      }
    })();
    return () => {
      annule = true;
    };
  }, [orgId, estPiloteDeLOrg, isLoading]);

  const plusTard = () => {
    if (orgId) reportees.add(orgId);
    setOuvert(false);
  };

  async function accepter() {
    if (!version) return;
    setBusy(true);
    try {
      await api.post("/conditions-usage/accepter", { documentLegalId: version.id });
      toast.success("Conditions d'usage acceptées au nom de l'organisation");
      setOuvert(false);
    } catch (e) {
      toast.error(messageApi(e, "Acceptation impossible"));
    } finally {
      setBusy(false);
    }
  }

  if (!ouvert || !version) return null;
  const nomOrg = user?.organisationNom || "mon organisation";

  return (
    <DialogMobile open onClose={plusTard} maxWidth="md" scroll="paper">
      <DialogTitle>{version.titre}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip size="small" label={`Version ${version.version}`} />
          <Chip size="small" variant="outlined" label={`En vigueur depuis le ${formatDateShortFR(version.dateEffet)}`} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pairsform est mis à la disposition de {nomOrg} sans redevance d'usage ; en contrepartie, les données
          produites contribuent, pseudonymisées et agrégées, à la recherche sur le codéveloppement. L'acceptation
          est datée et porte sur cette version du texte.
        </Typography>
        {texte === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>
        ) : (
          <Typography
            component="div"
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, maxHeight: { md: 420 }, overflowY: "auto", p: 2, bgcolor: "action.hover", borderRadius: 1 }}
          >
            {texte}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Le texte reste consultable à tout moment sur{" "}
          <MuiLink href="/conditions-usage" target="_blank" rel="noopener noreferrer">la page des conditions d'usage</MuiLink>.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={plusTard} disabled={busy} sx={{ textTransform: "none" }}>Plus tard</Button>
        <Button variant="contained" onClick={accepter} disabled={busy || texte === null} sx={{ textTransform: "none" }}>
          J'accepte au nom de {nomOrg}
        </Button>
      </DialogActions>
    </DialogMobile>
  );
}
